// Simulator data: hand rankings, preflop chart, postflop scenarios.

export const HAND_RANKS = [
  { key: "high-card", label: "High Card", rank: 1 },
  { key: "pair", label: "One Pair", rank: 2 },
  { key: "two-pair", label: "Two Pair", rank: 3 },
  { key: "three-kind", label: "Three of a Kind", rank: 4 },
  { key: "straight", label: "Straight", rank: 5 },
  { key: "flush", label: "Flush", rank: 6 },
  { key: "full-house", label: "Full House", rank: 7 },
  { key: "four-kind", label: "Four of a Kind", rank: 8 },
  { key: "straight-flush", label: "Straight Flush", rank: 9 },
];

// A small set of Mode-A hand evaluation puzzles.
// Each item: 5 cards (rank+suit) and correct rank key.
export const HAND_QUIZZES = [
  { cards: ["A♠", "K♠", "Q♠", "J♠", "T♠"], answer: "straight-flush", explain: "Royal-flush is the top straight-flush." },
  { cards: ["9♥", "9♦", "9♣", "4♠", "4♥"], answer: "full-house", explain: "Three 9s plus a pair of 4s = full house." },
  { cards: ["2♦", "3♦", "5♦", "8♦", "Q♦"], answer: "flush", explain: "All five diamonds — flush." },
  { cards: ["6♣", "7♦", "8♠", "9♥", "T♣"], answer: "straight", explain: "T-9-8-7-6 in mixed suits = straight." },
  { cards: ["K♦", "K♥", "K♠", "5♣", "2♠"], answer: "three-kind", explain: "Three kings, no pair alongside." },
  { cards: ["J♠", "J♦", "4♣", "4♦", "9♥"], answer: "two-pair", explain: "Jacks and fours — two pair." },
  { cards: ["7♠", "7♥", "A♣", "K♦", "2♠"], answer: "pair", explain: "Only one pair of sevens." },
  { cards: ["A♥", "K♦", "9♣", "6♠", "3♥"], answer: "high-card", explain: "No pair, no draw — Ace high." },
  { cards: ["Q♣", "Q♦", "Q♥", "Q♠", "2♦"], answer: "four-kind", explain: "Four queens." },
];

// Pot-odds micro-quiz: given call amount and pot size, compute required equity %.
export const POT_ODDS = [
  { pot: 100, toCall: 50, answer: 25, explain: "50 to call into 100+50 = 150 pot → 50/(150+50) = 25%." },
  { pot: 200, toCall: 100, answer: 25, explain: "100 to call into 200+100 = 300 pot → 100/(300+100) = 25%." },
  { pot: 80, toCall: 20, answer: 20, explain: "20 to call into 80+20 = 100 pot → 20/(100+20) ≈ 20%." },
  { pot: 300, toCall: 300, answer: 33, explain: "Full pot-sized bet → 300/(600+300) ≈ 33%." },
  { pot: 60, toCall: 60, answer: 33, explain: "Full pot-sized bet → 33%." },
];

// Simplified preflop chart: for each position, list open ranges.
// Positions: EP (UTG), MP, CO (cutoff), BTN (button), SB, BB.
// action: "raise" | "call" | "fold"
export const PREFLOP_CHART = {
  EP: {
    raise: ["AA", "KK", "QQ", "JJ", "TT", "99", "AKs", "AQs", "AJs", "KQs", "AKo"],
    call: ["88", "77", "AJo", "KQo"],
  },
  MP: {
    raise: ["AA", "KK", "QQ", "JJ", "TT", "99", "88", "77", "AKs", "AQs", "AJs", "ATs", "KQs", "KJs", "AKo", "AQo"],
    call: ["66", "55", "KTs", "QJs", "AJo"],
  },
  CO: {
    raise: ["AA", "KK", "QQ", "JJ", "TT", "99", "88", "77", "66", "55",
      "AKs", "AQs", "AJs", "ATs", "A9s", "KQs", "KJs", "KTs", "QJs", "QTs", "JTs",
      "AKo", "AQo", "AJo", "KQo"],
    call: ["44", "33", "22", "A8s", "K9s", "T9s", "ATo"],
  },
  BTN: {
    raise: ["AA", "KK", "QQ", "JJ", "TT", "99", "88", "77", "66", "55", "44", "33", "22",
      "AKs", "AQs", "AJs", "ATs", "A9s", "A8s", "A7s", "A6s", "A5s", "A4s", "A3s", "A2s",
      "KQs", "KJs", "KTs", "K9s", "QJs", "QTs", "Q9s", "JTs", "J9s", "T9s", "98s", "87s", "76s",
      "AKo", "AQo", "AJo", "ATo", "KQo", "KJo", "QJo", "JTo"],
    call: [],
  },
  SB: {
    raise: ["AA", "KK", "QQ", "JJ", "TT", "99", "88", "77",
      "AKs", "AQs", "AJs", "ATs", "KQs", "KJs", "QJs", "JTs",
      "AKo", "AQo", "AJo", "KQo"],
    call: [],
  },
  BB: {
    raise: ["AA", "KK", "QQ", "JJ", "AKs", "AKo"],
    call: ["TT", "99", "88", "77", "66", "55", "AQs", "AJs", "ATs", "KQs", "KJs", "AQo", "AJo", "KQo", "QJs", "JTs"],
  },
};

export const POSITIONS = ["EP", "MP", "CO", "BTN", "SB", "BB"];
export const RANKS = ["A", "K", "Q", "J", "T", "9", "8", "7", "6", "5", "4", "3", "2"];
export const SUITS = ["♠", "♥", "♦", "♣"];

// Build hand key like "AKs", "77", "AQo".
export function handKey(r1, r2, suited) {
  const order = RANKS;
  const a = order.indexOf(r1);
  const b = order.indexOf(r2);
  if (r1 === r2) return r1 + r2;
  const [hi, lo] = a < b ? [r1, r2] : [r2, r1];
  return hi + lo + (suited ? "s" : "o");
}

export function correctAction(position, r1, r2, suited) {
  const key = handKey(r1, r2, suited);
  const chart = PREFLOP_CHART[position];
  if (chart.raise.includes(key)) return "raise";
  if (chart.call.includes(key)) return "call";
  return "fold";
}

export function randomHand() {
  const r1 = RANKS[Math.floor(Math.random() * RANKS.length)];
  let r2 = RANKS[Math.floor(Math.random() * RANKS.length)];
  const suited = r1 !== r2 && Math.random() < 0.5;
  const position = POSITIONS[Math.floor(Math.random() * POSITIONS.length)];
  return { r1, r2, suited, position };
}

// Mode C: scripted postflop scenarios with a decision point.
export const POSTFLOP_SCENARIOS = [
  {
    id: "wet-board-cbet",
    title: "Wet Board C-Bet",
    hero: ["A♠", "K♠"],
    board: ["Q♠", "T♦", "5♠"],
    situation: "You raised UTG, big blind called. Board is Q♠ T♦ 5♠. Villain checks. You have nut flush draw + gutshot + two overcards.",
    equity: 62,
    choices: [
      { key: "bet", label: "Bet 66% pot", correct: true, explain: "Your equity is high (~62%) and the board favors your range. Charge the draws." },
      { key: "check", label: "Check back", correct: false, explain: "Way too passive — you deny value from Qx and free-roll draws that should pay." },
      { key: "shove", label: "Overbet all-in", correct: false, explain: "Overbet folds out worse and lets villain 3-bet you off the equity." },
    ],
  },
  {
    id: "turn-barrel",
    title: "Turn Barrel Spot",
    hero: ["A♣", "Q♦"],
    board: ["Q♣", "8♥", "3♠", "K♦"],
    situation: "You c-bet flop, villain called. Turn K puts a scare card up. You still have top pair + top kicker.",
    equity: 55,
    choices: [
      { key: "bet", label: "Bet 60% pot", correct: true, explain: "The K helps YOUR range more than villain's calling range. Keep barreling for value + fold equity." },
      { key: "check", label: "Check", correct: false, explain: "Checking gives up value on a card that hits your range." },
      { key: "fold", label: "Fold", correct: false, explain: "Fold with top pair top kicker on the turn? Never." },
    ],
  },
  {
    id: "river-decision",
    title: "River Bluff-Catch",
    hero: ["A♥", "A♦"],
    board: ["Q♦", "J♠", "9♣", "5♥", "6♠"],
    situation: "Villain check-raised you on the flop, called turn, then leads big on the river. Pocket aces have basically one pair now on a busy board.",
    equity: 35,
    choices: [
      { key: "call", label: "Call", correct: true, explain: "Aces are a bluff-catcher here. Villain reps a lot of missed draws — call, don't raise." },
      { key: "raise", label: "Raise", correct: false, explain: "Raising folds out the bluffs and only gets called by better." },
      { key: "fold", label: "Fold", correct: false, explain: "Folding aces after committing 3 streets is too tight against a polarized range." },
    ],
  },
];
